import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import FileUpload from './FileUpload';
import ValidationResults from './ValidationResults';
import SOFFieldsDisplay from './SOFFieldsDisplay';
import ComparisonModal from './ComparisonModal';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import ModuleAuditDownloadButton from './shared/ModuleAuditDownloadButton.tsx';

function ValidateSOF({ user }) {
  const [validationResult, setValidationResult] = useState(null);
  const [chargebeeInstances, setChargebeeInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState('');
  const [loading, setLoading] = useState(false);
  const [sofData, setSofData] = useState(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadDate, setUploadDate] = useState(null);

  useEffect(() => {
    fetchChargebeeInstances();
  }, []);

  const fetchChargebeeInstances = async () => {
    try {
      const response = await axiosInstance.get('/api/chargebee-instances');
      setChargebeeInstances(response.data);
      if (response.data.length > 0) {
        const testInstance = response.data.find(instance => instance.id === 'test');
        if (testInstance) {
          setSelectedInstance('test');
        } else {
          setSelectedInstance(response.data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching Chargebee instances:', error);
    }
  };

  const handleFileUpload = async (file) => {
    setLoading(true);
    setUploadedFile(file);
    setUploadDate(new Date().toISOString());
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axiosInstance.post('/api/parse-sof-fields', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('SOF fields extracted:', response.data);
      setSofData(response.data);
    } catch (error) {
      console.error('Error parsing SOF:', error);
      alert(resolve403ErrorMessage(error, 'Error parsing SOF. Please check the file and try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async () => {
    alert('Subscription creation is not available in SOF fields mode.');
  };

  const handleCompareToChargebee = async () => {
    setLoading(true);
    
    try {
      const response = await axiosInstance.post('/api/compare-sof-to-chargebee', {
        sof_data: sofData,
        chargebee_instance: selectedInstance || 'test'
      });
      
      setComparisonResult(response.data);
      setShowComparisonModal(true);
    } catch (error) {
      console.error('Error comparing SOF to Chargebee:', error);
      alert(resolve403ErrorMessage(
        error,
        'Failed to compare SOF with Chargebee: ' + (error.response?.data?.error || error.message)
      ));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">
          Comparing Sales Order Form to Chargebee subscription
        </h1>
        <ModuleAuditDownloadButton
          resourceType="sof_comparison"
          moduleKey={MODULE_KEYS.SOF}
          filenamePrefix="sof_comparison_audit"
        />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Upload Sales Order Form
        </h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Chargebee Instance
          </label>
          <select
            value={selectedInstance}
            onChange={(e) => setSelectedInstance(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {chargebeeInstances.map((instance) => (
              <option key={instance.id} value={instance.id}>
                {instance.name} ({instance.region})
              </option>
            ))}
          </select>
        </div>

        <FileUpload onFileUpload={handleFileUpload} loading={loading} />
      </div>

      {sofData && (
        <SOFFieldsDisplay 
          sofData={sofData} 
          onCompareToChargebee={handleCompareToChargebee}
        />
      )}

      {validationResult && (
        <ValidationResults
          result={validationResult}
          onCreateSubscription={handleCreateSubscription}
          loading={loading}
        />
      )}

      {showComparisonModal && comparisonResult && (
        <ComparisonModal
          comparisonResult={comparisonResult}
          uploadedFile={uploadedFile}
          uploadDate={uploadDate}
          user={user}
          onClose={() => setShowComparisonModal(false)}
        />
      )}
    </div>
  );
}

export default ValidateSOF;